import { useState, useCallback, useMemo } from "react";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { SmilePlus } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useAuth } from "@/contexts/auth-context";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";

const PULSE_EMOJI_OPTIONS = ['👍', '👏', '✅', '❌', '😂', '😊', '😉', '😢', '🔥', '🙏'] as const;

interface ReactionEntry {
  id: string;
  targetType: string;
  targetId: string;
  reactorId: number;
  type: 'emoji' | 'text';
  content: string;
  createdAt: Date | string;
  reactor?: {
    id: number;
    firstName?: string | null;
    shortCode?: string | null;
    colorCode?: string | null;
  };
}

interface PulseReactionBarProps {
  pulseId: number;
  projectId?: string;
  className?: string;
  side?: "top" | "bottom" | "left" | "right";
}

export function PulseReactionBar({ pulseId, projectId, className, side = "top" }: PulseReactionBarProps) {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const currentUserId = user?.id || 0;

  const addReactionMutation = useMutation({
    mutationFn: async (emoji: string) => {
      return apiRequest('POST', '/api/reactions', {
        targetType: 'pulse',
        targetId: String(pulseId),
        type: 'emoji',
        content: emoji,
      });
    },
    onMutate: async (emoji: string) => {
      const bulkPrefix = ['/api/reactions/bulk', 'pulse', projectId];
      const singleKey = ['/api/reactions', 'pulse', String(pulseId)];

      await queryClient.cancelQueries({ queryKey: bulkPrefix });
      await queryClient.cancelQueries({ queryKey: singleKey });

      const bulkSnapshots = queryClient.getQueriesData<Record<string, ReactionEntry[]>>({ queryKey: bulkPrefix });
      const previousSingle = queryClient.getQueryData<ReactionEntry[]>(singleKey);

      const optimisticReaction: ReactionEntry = {
        id: `optimistic-${Date.now()}`,
        targetType: 'pulse',
        targetId: String(pulseId),
        reactorId: currentUserId,
        type: 'emoji',
        content: emoji,
        createdAt: new Date().toISOString(),
        reactor: {
          id: currentUserId,
          firstName: user?.firstName || user?.username || 'You',
          shortCode: user?.shortCode || null,
          colorCode: user?.colorCode || null,
        },
      };

      const replaceUserReaction = (existing: ReactionEntry[]): ReactionEntry[] => {
        const withoutUser = existing.filter(r => r.reactorId !== currentUserId);
        return [...withoutUser, optimisticReaction];
      };

      for (const [key, data] of bulkSnapshots) {
        if (data) {
          const updated = { ...data };
          updated[String(pulseId)] = replaceUserReaction(updated[String(pulseId)] || []);
          queryClient.setQueryData(key, updated);
        }
      }

      if (previousSingle) {
        queryClient.setQueryData(singleKey, replaceUserReaction(previousSingle));
      }

      return { bulkSnapshots, previousSingle };
    },
    onError: (_err, _emoji, context) => {
      if (context?.bulkSnapshots) {
        for (const [key, data] of context.bulkSnapshots) {
          queryClient.setQueryData(key, data);
        }
      }
      if (context?.previousSingle) {
        queryClient.setQueryData(['/api/reactions', 'pulse', String(pulseId)], context.previousSingle);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/reactions/bulk', 'pulse', projectId] });
      queryClient.invalidateQueries({ queryKey: ['/api/reactions', 'pulse', String(pulseId)] });
    },
  });

  const handleEmojiClick = useCallback((emoji: string) => {
    addReactionMutation.mutate(emoji);
    setOpen(false);
  }, [addReactionMutation]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <span
          role="button"
          tabIndex={0}
          className={cn(
            "inline-flex items-center justify-center h-5 w-5 rounded-full bg-slate-700/80 hover:bg-slate-600 shadow-sm border border-white/10 transition-colors cursor-pointer",
            className
          )}
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(true); } }}
        >
          <SmilePlus className="h-3 w-3 text-white/80" />
        </span>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto p-2 bg-gray-800 border-gray-600"
        side={side}
        align="center"
        sideOffset={4}
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <div className="flex gap-1">
          {PULSE_EMOJI_OPTIONS.map((emoji) => (
            <button
              key={emoji}
              onClick={(e) => {
                e.stopPropagation();
                handleEmojiClick(emoji);
              }}
              className="text-lg hover:scale-125 transition-transform p-1 rounded hover:bg-gray-700"
              disabled={addReactionMutation.isPending}
            >
              {emoji}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

interface PulseReactionDisplayProps {
  reactions: ReactionEntry[];
  pulseId: number;
  projectId?: string;
  maxVisible?: number;
  compact?: boolean;
}

export function PulseReactionDisplay({ reactions, pulseId, projectId, maxVisible = 6, compact = false }: PulseReactionDisplayProps) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const currentUserId = user?.id || 0;

  const toggleReactionMutation = useMutation({
    mutationFn: async ({ content, hasIt }: { content: string; hasIt: boolean }) => {
      if (hasIt) {
        return apiRequest('DELETE', '/api/reactions', {
          targetType: 'pulse',
          targetId: String(pulseId),
          content,
        });
      } else {
        return apiRequest('POST', '/api/reactions', {
          targetType: 'pulse',
          targetId: String(pulseId),
          type: 'emoji',
          content,
        });
      }
    },
    onMutate: async ({ content, hasIt }) => {
      const bulkPrefix = ['/api/reactions/bulk', 'pulse', projectId];
      const singleKey = ['/api/reactions', 'pulse', String(pulseId)];

      await queryClient.cancelQueries({ queryKey: bulkPrefix });
      await queryClient.cancelQueries({ queryKey: singleKey });

      const bulkSnapshots = queryClient.getQueriesData<Record<string, ReactionEntry[]>>({ queryKey: bulkPrefix });
      const previousSingle = queryClient.getQueryData<ReactionEntry[]>(singleKey);

      const updateReactions = (existing: ReactionEntry[]): ReactionEntry[] => {
        if (hasIt) {
          return existing.filter(r => !(r.reactorId === currentUserId && r.content === content));
        } else {
          const withoutUser = existing.filter(r => r.reactorId !== currentUserId);
          return [...withoutUser, {
            id: `optimistic-${Date.now()}`,
            targetType: 'pulse',
            targetId: String(pulseId),
            reactorId: currentUserId,
            type: 'emoji' as const,
            content,
            createdAt: new Date().toISOString(),
            reactor: {
              id: currentUserId,
              firstName: user?.firstName || user?.username || 'You',
              shortCode: user?.shortCode || null,
              colorCode: user?.colorCode || null,
            },
          }];
        }
      };

      for (const [key, data] of bulkSnapshots) {
        if (data) {
          const updated = { ...data };
          updated[String(pulseId)] = updateReactions(updated[String(pulseId)] || []);
          queryClient.setQueryData(key, updated);
        }
      }

      if (previousSingle) {
        queryClient.setQueryData(singleKey, updateReactions(previousSingle));
      }

      return { bulkSnapshots, previousSingle };
    },
    onError: (_err, _vars, context) => {
      if (context?.bulkSnapshots) {
        for (const [key, data] of context.bulkSnapshots) {
          queryClient.setQueryData(key, data);
        }
      }
      if (context?.previousSingle) {
        queryClient.setQueryData(['/api/reactions', 'pulse', String(pulseId)], context.previousSingle);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/reactions/bulk', 'pulse', projectId] });
      queryClient.invalidateQueries({ queryKey: ['/api/reactions', 'pulse', String(pulseId)] });
    },
  });

  if (!reactions || reactions.length === 0) return null;

  const visibleReactions = reactions.slice(0, maxVisible);
  const overflowCount = reactions.length - maxVisible;
  const overflowReactions = reactions.slice(maxVisible);

  return (
    <TooltipProvider delayDuration={200}>
      <div className={cn("inline-flex items-center gap-px", compact && "gap-0")} onClick={(e) => e.stopPropagation()}>
        {visibleReactions.map((reaction) => {
          const reactorName = reaction.reactor?.firstName || reaction.reactor?.shortCode || 'User';
          const isOwn = reaction.reactorId === currentUserId;
          const currentUserHasThisEmoji = reactions.some(r => r.reactorId === currentUserId && r.content === reaction.content);

          return (
            <Tooltip key={reaction.id}>
              <TooltipTrigger asChild>
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    if (!toggleReactionMutation.isPending) {
                      toggleReactionMutation.mutate({ content: reaction.content, hasIt: currentUserHasThisEmoji });
                    }
                  }}
                  onKeyDown={(e) => {
                    if ((e.key === 'Enter' || e.key === ' ') && !toggleReactionMutation.isPending) {
                      e.stopPropagation();
                      e.preventDefault();
                      toggleReactionMutation.mutate({ content: reaction.content, hasIt: currentUserHasThisEmoji });
                    }
                  }}
                  className={cn(
                    "inline-flex items-center justify-center w-5 h-5 rounded text-xs leading-none cursor-pointer transition-colors select-none",
                    isOwn
                      ? "bg-blue-500/25 ring-1 ring-blue-500/50 hover:bg-blue-500/40"
                      : "hover:bg-gray-600/60"
                  )}
                >
                  {reaction.content}
                </span>
              </TooltipTrigger>
              <TooltipContent side="top" className="text-xs bg-gray-900 border-gray-700 text-gray-200">
                {reactorName}
                {isOwn ? " (click to remove)" : currentUserHasThisEmoji ? " (click to remove yours)" : " (click to react)"}
              </TooltipContent>
            </Tooltip>
          );
        })}
        {overflowCount > 0 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="text-[9px] text-gray-500 px-0.5 cursor-default">
                +{overflowCount}
              </span>
            </TooltipTrigger>
            <TooltipContent side="top" className="text-xs bg-gray-900 border-gray-700 text-gray-200 max-w-[200px]">
              {overflowReactions.map(r => {
                const name = r.reactor?.firstName || r.reactor?.shortCode || 'User';
                return `${r.content} ${name}`;
              }).join(', ')}
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </TooltipProvider>
  );
}

export function usePulseReactionsBulk(pulseIds: number[], projectId: string) {
  const sortedIds = useMemo(() => [...pulseIds].sort((a, b) => a - b), [pulseIds]);
  const idsKey = sortedIds.join(',');
  return useQuery<Record<string, ReactionEntry[]>>({
    queryKey: ['/api/reactions/bulk', 'pulse', projectId, idsKey],
    queryFn: async () => {
      if (sortedIds.length === 0) return {};
      const response = await apiRequest('POST', '/api/reactions/bulk', {
        targetType: 'pulse',
        targetIds: sortedIds.map(String),
      });
      return await response.json();
    },
    enabled: sortedIds.length > 0,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });
}

export function usePulseReactionsSingle(pulseId: number) {
  return useQuery<ReactionEntry[]>({
    queryKey: ['/api/reactions', 'pulse', String(pulseId)],
    enabled: pulseId > 0,
    staleTime: 30_000,
  });
}

interface PulseReactionsInlineProps {
  pulseId: number;
  projectId: string;
  maxVisible?: number;
}

export function PulseReactionsInline({ pulseId, projectId, maxVisible = 6 }: PulseReactionsInlineProps) {
  const { data: reactions = [] } = usePulseReactionsSingle(pulseId);

  return (
    <div className="flex items-center gap-1">
      {reactions.length > 0 && (
        <PulseReactionDisplay
          reactions={reactions}
          pulseId={pulseId}
          projectId={projectId}
          maxVisible={maxVisible}
        />
      )}
      <PulseReactionBar
        pulseId={pulseId}
        projectId={projectId}
        side="bottom"
      />
    </div>
  );
}
